import React from 'react';
import { Home, PanelsTopLeft } from 'lucide-react';
import { HiOutlineSquare3Stack3D } from 'react-icons/hi2';
import TextTooltip from '../common/TextTooltip';
import { resolvePricingColumn } from '../../lib/supabase/finalPricingModes';

export const formatMoney = (value) => {
    const amount = Number(value);
    if (!Number.isFinite(amount)) return '—';

    return new Intl.NumberFormat('en-AU', {
        style: 'currency',
        currency: 'AUD',
        minimumFractionDigits: 2,
    }).format(amount);
};

export const formatMoneyCompact = (value) => {
    const amount = Number(value);
    if (!Number.isFinite(amount)) return '—';

    return new Intl.NumberFormat('en-AU', {
        style: 'currency',
        currency: 'AUD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(amount);
};

export const DEPT_META = {
    roof: {
        label: 'Roof',
        icon: Home,
        iconOn: 'text-orange-500',
        on: 'border-orange-200 bg-orange-50 text-orange-700',
        off: 'border-gray-200 bg-gray-50 text-gray-400',
    },
    wall: {
        label: 'Wall',
        icon: PanelsTopLeft,
        iconOn: 'text-blue-500',
        on: 'border-blue-200 bg-blue-50 text-blue-700',
        off: 'border-gray-200 bg-gray-50 text-gray-400',
    },
    floor: {
        label: 'Floor',
        icon: HiOutlineSquare3Stack3D,
        iconOn: 'text-emerald-500',
        on: 'border-emerald-200 bg-emerald-50 text-emerald-700',
        off: 'border-gray-200 bg-gray-50 text-gray-400',
    },
};

export const SOURCE_META = {
    estimation: {
        tag: 'E',
        border: 'border-amber-200',
        bg: 'bg-amber-50',
        text: 'text-amber-700',
        tagText: 'text-amber-500',
        label: 'Estimated',
    },
    department: {
        tag: 'D',
        border: 'border-purple-200',
        bg: 'bg-purple-50',
        text: 'text-purple-700',
        tagText: 'text-purple-500',
        label: 'Reviewed',
    },
    final: {
        tag: 'F',
        border: 'border-emerald-200',
        bg: 'bg-emerald-50',
        text: 'text-emerald-700',
        tagText: 'text-emerald-500',
        label: 'Final',
    },
};

const SOURCE_LABEL_PREFIX = { all: '', estimation: 'Est.', department: 'Designer', final: 'Final' };
const GROUPING_LABEL = { total: 'Total', 'per-department': 'Departments', 'per-record': 'Records' };

// Short composed label used both as the table-column header and inside the
// Columns popover's list of configured columns (e.g. "Est. Total",
// "Designer Departments", "Records").
export const getColumnLabel = ({ source, grouping }) => {
    const prefix = SOURCE_LABEL_PREFIX[source];
    return prefix ? `${prefix} ${GROUPING_LABEL[grouping]}` : GROUPING_LABEL[grouping];
};

const TotalValue = React.memo(({ total }) => (
    <span className="inline-flex items-center gap-1 rounded border border-gray-200 bg-gray-50 px-1.5 py-0.5 text-[10px] font-semibold tabular-nums text-gray-700">
        {total !== null ? formatMoney(total) : '—'}
    </span>
));

const PerDepartmentValue = React.memo(({ departments }) => (
    <div className="flex items-center gap-1 flex-wrap">
        {departments.map(({ key, amount, priced }) => {
            const meta = DEPT_META[key];
            const Icon = meta.icon;

            return (
                <TextTooltip
                    key={key}
                    text={priced ? `${meta.label}: ${formatMoney(amount)}` : `${meta.label}: not entered`}
                >
                    <span
                        className={`inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[10px] font-semibold tabular-nums ${priced ? meta.on : meta.off}`}
                    >
                        <Icon className={`w-3 h-3 shrink-0 ${priced ? meta.iconOn : 'text-gray-300'}`} />
                        {priced ? formatMoneyCompact(amount) : '—'}
                    </span>
                </TextTooltip>
            );
        })}
    </div>
));

const PerRecordValue = React.memo(({ lines, showSourceTag }) => (
    <div className="flex items-center gap-1 flex-wrap">
        {lines.length === 0 ? (
            <span className="text-[10px] text-gray-300">—</span>
        ) : (
            lines.map(line => {
                const sourceMeta = SOURCE_META[line.source] || SOURCE_META.estimation;

                return (
                    <TextTooltip
                        key={line.id}
                        text={`${sourceMeta.label} · ${line.label}${line.unit_number ? ` (${line.unit_number})` : ''}: ${formatMoney(line.value)}`}
                    >
                        <span className={`inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[10px] font-semibold tabular-nums ${sourceMeta.border} ${sourceMeta.bg} ${sourceMeta.text}`}>
                            {showSourceTag && <span className={`font-bold ${sourceMeta.tagText}`}>{sourceMeta.tag}</span>}
                            <span>{line.abbreviation}</span>
                            {formatMoneyCompact(line.value)}
                        </span>
                    </TextTooltip>
                );
            })
        )}
    </div>
));

/**
 * Renders one configured pricing column's value for a job (or, when
 * `department` is passed, for a single department row on the Quotation
 * list's `separate` view). Resolves the column via
 * finalPricingModes.js's resolvePricingColumn and dispatches on its
 * `type`. The E/D/F source tag on per-record chips is only shown when the
 * column's own source is `all` — a column already filtered to one source
 * doesn't need to repeat it on every chip.
 */
const PricingColumnCell = React.memo(({ column, estimationTotals, effectivePricing, requireFinalPricing, department = null }) => {
    const resolved = resolvePricingColumn({
        source: column.source,
        grouping: column.grouping,
        estimationTotals,
        effectivePricing,
        requireFinalPricing,
        department,
    });

    if (resolved.type === 'total') return <TotalValue total={resolved.total} />;
    if (resolved.type === 'per-department') return <PerDepartmentValue departments={resolved.departments} />;
    return <PerRecordValue lines={resolved.lines} showSourceTag={column.source === 'all'} />;
});

export default PricingColumnCell;
